<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Auto-Pay" hide-footer>
        <div v-if="loading" class="text-center p-3">
            <b-spinner small /> Loading...
        </div>

        <template v-else>
            <b-alert :show="!autoPayAvailable" variant="warning" class="mb-2">
                Auto-pay is not enabled for this pharmacy.
            </b-alert>

            <!-- The disable path is always available for an already-enrolled account so it can be
                 unenrolled even after the pharmacy turns auto-pay off; only enrolling is gated on
                 availability. Keyed off the loaded enrollment (not the live checkbox) so toggling
                 off doesn't hide the Save button before the change can be saved. -->
            <template v-if="autoPayAvailable || wasEnrolled">
                <b-form-checkbox v-model="enabled" switch class="mb-3">
                    Enroll this account in auto-pay
                </b-form-checkbox>

                <b-form-group v-if="enabled && autoPayAvailable" label="Payment method">
                    <b-select v-model="stripeCardId" :options="cardOptions" />
                    <small v-if="!cards.length" class="text-muted">
                        The responsible patient has no saved cards on file. A card must be added before
                        enrolling in auto-pay.
                    </small>
                </b-form-group>

                <div class="text-right">
                    <b-button size="sm" variant="primary" :disabled="saving || !canSave" @click="save">
                        Save
                    </b-button>
                </div>
            </template>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { ARSavedCard } from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "ARAutoPayModal",
})
export default class ARAutoPayModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;
    // Provided by the parent card, which already loaded the config to decide whether to show the
    // Auto-Pay button — passing it down avoids re-fetching config and state that can diverge.
    @Prop({ type: Boolean, default: false }) autoPayAvailable!: boolean;

    private loading = false;
    private saving = false;
    private enabled = false;
    // The account's persisted enrollment at load time. Drives whether the disable controls show, so
    // unchecking the box (setting enabled=false) doesn't hide Save before the change is saved.
    private wasEnrolled = false;
    private stripeCardId: string | null = null;
    private cards: ARSavedCard[] = [];

    get cardOptions(): { value: string | null; text: string }[] {
        const options = this.cards.map(c => ({
            value: c.id,
            text: `${c.brand ?? "Card"} •••• ${c.cardNumber ?? ""} (exp ${c.expMonth ?? "--"}/${c.expYear ?? "--"})`,
        }));
        return [{ value: null, text: "Select a card..." }, ...options];
    }

    get canSave(): boolean {
        // Unenrolling is always allowed; enrolling requires a selected card.
        return !this.enabled || !!this.stripeCardId;
    }

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible || !this.accountId) return;
        await this.load();
    }

    private async load(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.loading = true;
            const [status, cards] = await Promise.all([
                axios.get<{ enabled: boolean; stripeCardId: string | null }>(`/AR/accounts/${this.accountId}/autopay`),
                axios.get<ARSavedCard[]>(`/AR/accounts/${this.accountId}/payment-methods`).catch(() => ({ data: [] })),
            ]);
            this.enabled = status.data?.enabled ?? false;
            this.wasEnrolled = this.enabled;
            this.stripeCardId = status.data?.stripeCardId ?? null;
            this.cards = cards.data ?? [];
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    async save(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.put(`/AR/accounts/${this.accountId}/autopay`, {
                enabled: this.enabled,
                stripeCardId: this.enabled ? this.stripeCardId : null,
            });
            this.$notification(NotificationOptions.success("Auto-pay updated."));
            this.$emit("saved");
            this.onVisibleChange(false);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }
}
</script>

<style scoped></style>
